import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { api as axios } from '../utils/apiClient';
import Sidebar from './Sidebar';
import Uptime from './Uptime';
import PerformanceDetails from './PerformanceDetails';
import SeoTab from './SeoTab';
import Plugins from './Plugins';
import PluginLogs from './PluginLogs';
import SiteMonitor from './SiteMonitor';
import './SiteDetails.css';
import { getStoredProfile, clearProfile } from '../utils/profileStorage';
import { clearAuthSession, getStoredUser } from '../utils/authStorage';
import { hasWordPressReportedUpdate } from '../utils/pluginUtils';

const SiteDetails = () => {
  const { siteId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Check if navigation state has activeTab
    return location.state?.activeTab || 'uptime';
  });
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [errorPerformance, setErrorPerformance] = useState(null);
  const [sslInfo, setSslInfo] = useState(null);
  const [sslError, setSslError] = useState(null);
  const [malwareInfo, setMalwareInfo] = useState(null);
  const [malwareError, setMalwareError] = useState(null);
  const [loadingSecurity, setLoadingSecurity] = useState(false);
  const [profile, setProfile] = useState(() => getStoredProfile());
  const [pluginUpdateCount, setPluginUpdateCount] = useState(0);
  const [monitorInfo, setMonitorInfo] = useState(null);
  const [monitorStatus, setMonitorStatus] = useState(site?.monitorStatus?.status || 'unavailable');
  const [monitorStatusDetails, setMonitorStatusDetails] = useState(site?.monitorStatus?.status_details || null);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [monitorError, setMonitorError] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  const formatRole = (role) => {
    if (!role) return '';
    const roleLower = role.toLowerCase();
    if (roleLower === 'user') return 'Viewer';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  const fetchPerformanceData = async (forceFetch = false) => {
    console.log('Fetching performance data...', { forceFetch, siteId });
    setLoadingPerformance(true);
    setErrorPerformance(null);
    try {
      const url = `/api/sites/${siteId}/performance${forceFetch ? '?forceFetch=true' : ''}`;
      console.log('Performance API URL:', url);
      const response = await axios.get(url);
      console.log('Performance data fetched successfully:', {
        hasData: !!response.data,
        disabled: response.data?.disabled,
        message: response.data?.message,
        hasMobile: !!response.data?.mobile,
        hasDesktop: !!response.data?.desktop,
        mobileScore: response.data?.mobile?.performanceScore?.value,
        desktopScore: response.data?.desktop?.performanceScore?.value,
        fullData: response.data
      });
      setPerformanceData(response.data);
      
      // If the response indicates it's disabled, set an error message
      if (response.data?.disabled === true) {
        setErrorPerformance(response.data?.message || 'Performance scanning is currently unavailable.');
      }
    } catch (error) {
      console.error('Error fetching performance data:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error
      });
      setErrorPerformance(error.response?.data?.message || 'Failed to load performance data.');
    } finally {
      setLoadingPerformance(false);
      console.log('Finished fetching performance data.');
    }
  };

  useEffect(() => {
    // If site data is passed via navigation state, use it immediately to avoid loading flash
    if (location.state?.siteData) {
      setSite(location.state.siteData);
    }

    const fetchSiteDetails = async () => {
      try {
        const response = await axios.get(`/api/sites/${siteId}`);
        setSite(response.data);
      } catch (error) {
        console.error('Error fetching site details:', error);
      }
    };

    // Always fetch to get the latest data, but if we have state data, it won't show loading
    fetchSiteDetails();
    
    // Set active tab from navigation state if available
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [siteId, location.state]);

  // Fetch plugin update count for notification badge
  useEffect(() => {
    const fetchPluginUpdateCount = async () => {
      if (!siteId) return;
      try {
        const response = await axios.get(`/api/sites/${siteId}/plugins`);
        // Always calculate from actual plugin list to match WordPress backend exactly
        if (Array.isArray(response.data?.plugins) && response.data.plugins.length > 0) {
          // Use hasWordPressReportedUpdate() to match WordPress backend count exactly
          // This only counts plugins where WordPress core reports update.new_version > current version
          const updateCount = response.data.plugins.filter(hasWordPressReportedUpdate).length;
          setPluginUpdateCount(updateCount);
        } else if (response.data?.summary?.updates !== undefined) {
          // Fallback to backend summary if plugin list is empty
          // Backend summary also uses WordPress-only update detection
          setPluginUpdateCount(response.data.summary.updates);
        } else {
          setPluginUpdateCount(0);
        }
      } catch (error) {
        console.error('Error fetching plugin update count:', error);
        setPluginUpdateCount(0);
      }
    };

    fetchPluginUpdateCount();
    // Refresh update count every 60 seconds
    const interval = setInterval(fetchPluginUpdateCount, 60000);
    
    // Listen for plugin update events to refresh count immediately
    const handlePluginUpdate = () => {
      fetchPluginUpdateCount();
    };
    window.addEventListener('plugin-updated', handlePluginUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('plugin-updated', handlePluginUpdate);
    };
  }, [siteId]);

  const fetchMonitorInfo = useCallback(async (force = false) => {
    if (!siteId) return;
    try {
      setMonitorLoading(true);
      setMonitorError(null);
      const response = await axios.get(`/api/sites/${siteId}/monitor${force ? '?force=true' : ''}`);
      if (response.data?.success === false) {
        setMonitorError(response.data.error || 'Failed to load monitoring data');
        setMonitorInfo(null);
        setMonitorStatus(response.data.status || 'unavailable');
        setMonitorStatusDetails(response.data.status_details || null);
      } else {
        setMonitorInfo(response.data?.data || response.data);
        setMonitorStatus(response.data?.status || 'live');
        setMonitorStatusDetails(response.data?.status_details || null);
      }
    } catch (error) {
      console.error('Error fetching monitor info:', error);
      setMonitorError(error.response?.data?.message || error.message);
      setMonitorInfo(null);
      setMonitorStatus('unavailable');
      setMonitorStatusDetails(null);
    } finally {
      setMonitorLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchMonitorInfo();
  }, [fetchMonitorInfo]);

  useEffect(() => {
    const syncProfile = () => setProfile(getStoredProfile());
    window.addEventListener('profile-updated', syncProfile);
    return () => window.removeEventListener('profile-updated', syncProfile);
  }, []);

  // Handle click outside profile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProfileAction = (action) => {
    setShowProfileMenu(false);
    if (action === 'my-profile') {
      // Navigate to User Management Profile page
      navigate('/users#profile');
    } else if (action === 'sign-out') {
      clearAuthSession();
      clearProfile();
      navigate('/login', { replace: true });
    }
  };

  const userDisplayName = useMemo(() => {
    if (profile?.name?.trim()) return profile.name.trim();
    if (profile?.email?.trim()) return profile.email.trim();
    return 'WebMonitor User';
  }, [profile?.name, profile?.email]);

  const userEmail = profile?.email?.trim() || '';
  const authUser = getStoredUser();
  const userRole = authUser?.role ? formatRole(authUser.role) : '';
  const userRoleLower = authUser?.role?.toLowerCase() || '';
  const privilegedRoles = ['admin', 'manager', 'technician'];
  const hasFullAccess = privilegedRoles.includes(userRoleLower);

  const userAvatar = useMemo(() => {
    return authUser?.profilePicture || profile?.avatar || null;
  }, [authUser?.profilePicture, profile?.avatar]);

  const profileInitials = useMemo(() => {
    if (!profile?.name) return 'JD';
    return profile.name
      .split(' ')
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }, [profile?.name]);

  // Fetch performance data only when component mounts initially and when siteId changes
  useEffect(() => {
    // Only fetch if siteId is available and performanceData is not yet loaded
    // This ensures we don't refetch just because the 'site' object is updated
    if (siteId && !performanceData) {
        fetchPerformanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]); // Depend only on siteId to fetch when navigating between sites

  // Auto-redirect to Security Monitoring page when Security tab is selected
  useEffect(() => {
    if (activeTab === 'security' && site?._id) {
      const securityUrl = `/security?siteId=${site._id}`;
      navigate(securityUrl);
    }
  }, [activeTab, site?._id, navigate]);

  if (!site) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <div className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div style={{ textAlign: 'center', color: '#666' }}>Loading site details...</div>
        </div>
      </div>
    );
  }

  const renderSecurityTab = () => {
    // This will rarely render since we redirect, but keep it as fallback
    return (
      <div className="tab-content">
        <p>Redirecting to Security Monitoring page...</p>
      </div>
    );
  };

  const currentMonitorInfo = monitorInfo || site?.monitorData || null;
  const currentMonitorStatus = monitorStatus || site?.monitorStatus?.status || 'unavailable';
  const currentMonitorStatusDetails = monitorStatusDetails || site?.monitorStatus?.status_details || null;
  const summaryStatusTooltip = [
    `Status: ${currentMonitorStatus.toUpperCase()}`,
    `Last updated: ${currentMonitorStatusDetails?.last_updated ? new Date(currentMonitorStatusDetails.last_updated).toLocaleString() : site?.lastMonitorSync ? new Date(site.lastMonitorSync).toLocaleString() : 'N/A'}`,
    currentMonitorStatusDetails?.succeeded_endpoints?.length ? `✔ ${currentMonitorStatusDetails.succeeded_endpoints.join('\n✔ ')}` : '',
    currentMonitorStatusDetails?.failed_endpoints?.length ? `✖ ${currentMonitorStatusDetails.failed_endpoints.join('\n✖ ')}` : ''
  ].filter(Boolean).join('\n');

  // Overview performance scores:
  // Show BOTH mobile and desktop PageSpeed scores when available.
  // Fall back to any stored performanceData on the Site document.
  const overviewMobileScore =
    performanceData?.mobile?.performanceScore?.value ??
    site.performanceData?.mobile?.performanceScore?.value ??
    null;
  const overviewDesktopScore =
    performanceData?.desktop?.performanceScore?.value ??
    site.performanceData?.desktop?.performanceScore?.value ??
    null;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'uptime':
        return <Uptime site={site} />;
      case 'seo':
        return <div className="tab-content"><SeoTab url={site.url} /></div>;
      case 'performance':
        return (
          <PerformanceDetails
            performanceData={performanceData}
            loading={loadingPerformance}
            error={errorPerformance}
            onRescan={fetchPerformanceData}
          />
        );
      case 'security':
        return renderSecurityTab();
      case 'plugins':
        return <Plugins site={site} />;
      case 'logs':
        return <PluginLogs site={site} />;
      case 'monitor':
        return (
          <SiteMonitor
            site={site}
            initialData={currentMonitorInfo}
            initialStatus={currentMonitorStatus}
            initialStatusDetails={currentMonitorStatusDetails}
          />
        );
      default:
        return <Uptime site={site} />;
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="main-content">
        <div className="header">
          <div>
            <h1>Website Details - {site.name}</h1>
            <div className="site-info-meta">
              <div className="site-info-item">
                <span className="site-info-label">URL:</span>
                <a href={site.url} target="_blank" rel="noreferrer" className="site-info-value">
                  {site.url}
                </a>
              </div>
              <div className="site-info-item">
                <span className="site-info-label">Owner Email:</span>
                <span className="site-info-value">{site.ownerEmail || 'Not provided'}</span>
              </div>
            </div>
          </div>
          <div className="header-profile" ref={profileMenuRef}>
            <button type="button" className="header-icon-button" aria-label="Notifications">
              🔔
            </button>
            <button
              type="button"
              className="header-user-chip"
              onClick={() => setShowProfileMenu((prev) => !prev)}
              aria-haspopup="menu"
              aria-expanded={showProfileMenu}
              style={{ cursor: 'pointer', border: 'none', background: '#fff' }}
            >
              <span className="header-avatar">
                {userAvatar ? (
                  <img 
                    src={userAvatar} 
                    alt={userDisplayName}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  profileInitials
                )}
              </span>
              <span style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                lineHeight: '1.1'
              }}>
                <span className="header-user-name">{userDisplayName}</span>
                {userRole && <span style={{
                  fontSize: '0.75em',
                  color: '#6b7280',
                  fontWeight: 500
                }}>{userRole}</span>}
              </span>
              <span style={{ fontSize: '0.8em', color: '#7a7f85', marginLeft: '4px' }}>▾</span>
            </button>
            {showProfileMenu && (
              <div className="profile-dropdown" role="menu" style={{
                position: 'absolute',
                top: 'calc(100% + 10px)',
                right: 0,
                background: '#fff',
                border: '1px solid #ece8e1',
                borderRadius: '20px',
                boxShadow: '0 20px 40px rgba(15, 23, 42, 0.15)',
                padding: '13px',
                minWidth: '180px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                zIndex: 1000
              }}>
                <div style={{
                  borderBottom: '1px solid #ece8e1',
                  padding: '0 4px 8px',
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{ fontWeight: 600, margin: 0, color: '#111827', fontSize: '14px' }}>{userDisplayName}</p>
                  {userEmail && <p style={{ fontSize: '0.85em', margin: '2px 0 0', color: '#6b7280' }}>{userEmail}</p>}
                  {userRole && <p style={{ fontSize: '0.85em', margin: '2px 0 0', color: '#6b7280' }}>{userRole}</p>}
                </div>
                {hasFullAccess && (
                  <button 
                    type="button" 
                    onClick={() => handleProfileAction('my-profile')}
                    style={{
                      padding: '8px 12px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'center',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#111827',
                      borderRadius: '3px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    My Profile
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleProfileAction('sign-out')}
                  style={{
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#dc3545',
                    borderRadius: '3px',
                    transition: 'background-color 0.2s',
                    marginTop: '4px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#fee2e2'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="monitor-summary">
          <div className="summary-header">
            <div>
              <h2>WordPress Overview</h2>
              {monitorError && <p className="summary-error">{monitorError}</p>}
            </div>
            <div className="summary-actions">
              <span
                className={`status-badge status-${currentMonitorStatus}`}
                title={summaryStatusTooltip}
              >
                {currentMonitorStatus.toUpperCase()}
              </span>
            <button
              className="btn-summary-refresh"
              onClick={() => fetchMonitorInfo(true)}
              disabled={monitorLoading}
            >
              {monitorLoading ? 'Syncing...' : 'Sync Overview'}
            </button>
          </div>
          </div>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">WP Version</span>
              <span className="summary-value">{currentMonitorInfo?.wp_version || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">PHP Version</span>
              <span className="summary-value">{currentMonitorInfo?.php_version || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Active Theme</span>
              <span className="summary-value">{currentMonitorInfo?.active_theme?.name || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Plugins</span>
              <span className="summary-value">
                {currentMonitorInfo?.plugins?.length ?? currentMonitorInfo?.plugins_count ?? '0'}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Site Health</span>
              <span className="summary-value">
                {currentMonitorInfo?.site_health?.summary
                  ? `${currentMonitorInfo.site_health.summary.good || 0} good / ${
                      currentMonitorInfo.site_health.summary.critical || 0
                    } critical`
                  : 'N/A'}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Performance (Mobile / Desktop)</span>
              <span className="summary-value">
                {overviewMobileScore !== null || overviewDesktopScore !== null
                  ? `M ${overviewMobileScore !== null && overviewMobileScore !== undefined
                      ? Math.round(overviewMobileScore)
                      : 'N/A'}/100 · D ${overviewDesktopScore !== null && overviewDesktopScore !== undefined
                      ? Math.round(overviewDesktopScore)
                      : 'N/A'}/100`
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div className="tabs-container">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'uptime' ? 'active' : ''}`}
              onClick={() => setActiveTab('uptime')}
            >
              Uptime
            </button>
            <button 
              className={`tab ${activeTab === 'seo' ? 'active' : ''}`}
              onClick={() => setActiveTab('seo')}
            >
              SEO
            </button>
            <button 
              className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
              onClick={() => setActiveTab('performance')}
            >
              Performance
            </button>
            <button 
              className={`tab ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              Security
            </button>
            <button 
              className={`tab ${activeTab === 'plugins' ? 'active' : ''}`}
              onClick={() => setActiveTab('plugins')}
              style={{ position: 'relative' }}
            >
              Plugins
              {pluginUpdateCount > 0 && (
                <span className="plugin-update-badge">{pluginUpdateCount}</span>
              )}
            </button>
            <button 
              className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              Log Info
            </button>
            <button 
              className={`tab ${activeTab === 'monitor' ? 'active' : ''}`}
              onClick={() => setActiveTab('monitor')}
            >
              Monitor
            </button>
          </div>
          <div className="tab-content-container">
            {renderTabContent()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SiteDetails; 